/**
 * Bill Split — frontend client.
 *
 * Pairs with backend module `bill_splits.py`. Provides:
 *   - REST API wrappers (create, get, join, claim, mark-paid)
 *   - useSplitStream() hook — SSE subscription with auto-reconnect
 *   - Payment deep-link builders for Venmo / CashApp / Apple Pay / Zelle
 *   - Currency formatting + light helpers
 *
 * Spec: /app/memory/BILL_SPLIT_SPEC.md
 */

import axios from "axios";
import { useEffect, useRef, useState } from "react";

const BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;

const client = axios.create({
  baseURL: BASE,
  withCredentials: true,  // session cookie for host endpoints
});

/* -------------------------------- REST -------------------------------- */

export async function createSplit({
  items,            // [{name, price_cents}]
  taxCents,
  tipCents,
  currency = "USD",
  tipLogEntryId = null,
  hostPaymentHandles = {},
}) {
  const { data } = await client.post("/splits", {
    items,
    tax_cents: taxCents,
    tip_cents: tipCents,
    currency,
    tip_log_entry_id: tipLogEntryId,
    host_payment_handles: hostPaymentHandles,
  });
  return data;
}

export async function getSplit(code) {
  const { data } = await client.get(`/splits/${code.toUpperCase()}`);
  return data;
}

export async function listMySplits() {
  const { data } = await client.get("/splits/mine");
  return data;
}

export async function joinSplit(code, name) {
  const { data } = await client.post(`/splits/${code.toUpperCase()}/join`, { name });
  return data; // { participant_id, split }
}

export async function claimItem(code, { itemId, participantId, unclaim = false }) {
  const { data } = await client.post(`/splits/${code.toUpperCase()}/claim`, {
    item_id: itemId,
    participant_id: participantId,
    unclaim,
  });
  return data;
}

export async function selfMarkPaid(code, participantId, unmark = false) {
  const { data } = await client.post(
    `/splits/${code.toUpperCase()}/participants/${participantId}/paid`,
    { unmark }
  );
  return data;
}

export async function hostMarkPaid(code, participantId, unmark = false) {
  const { data } = await client.patch(
    `/splits/${code.toUpperCase()}/participants/${participantId}/paid`,
    { unmark }
  );
  return data;
}

export async function closeSplit(code) {
  const { data } = await client.post(`/splits/${code.toUpperCase()}/close`);
  return data;
}

/* ----------------------------- Real-time hook ----------------------------- */

/**
 * Subscribe to a split session via SSE. Returns the latest snapshot,
 * a `setSplit` for optimistic updates, and a `reconnect` helper.
 *
 * EventSource auto-reconnects on network blips; we add an explicit
 * exponential-backoff fallback for HTTP 5xx returns that don't trigger
 * the default reconnect.
 */
export function useSplitStream(code) {
  const [split, setSplit] = useState(null);
  const [connected, setConnected] = useState(false);
  const [error, setError] = useState(null);
  const sourceRef = useRef(null);
  const backoffRef = useRef(1000);

  useEffect(() => {
    if (!code) return undefined;
    let cancelled = false;

    function open() {
      if (cancelled) return;
      const url = `${BASE}/splits/${code.toUpperCase()}/stream`;
      const es = new EventSource(url, { withCredentials: true });
      sourceRef.current = es;

      es.onopen = () => {
        setConnected(true);
        setError(null);
        backoffRef.current = 1000;  // reset backoff on healthy open
      };

      es.onmessage = (evt) => {
        try {
          const payload = JSON.parse(evt.data);
          setSplit(payload);
        } catch (e) {
          // Ignore comment/heartbeat lines that EventSource swallows anyway.
        }
      };

      es.onerror = () => {
        setConnected(false);
        es.close();
        if (cancelled) return;
        // Browser EventSource reconnects on 2xx errors automatically.
        // For 4xx/5xx it stops; manual exponential backoff up to 30s.
        const delay = Math.min(30000, backoffRef.current);
        backoffRef.current = Math.min(30000, backoffRef.current * 2);
        setTimeout(open, delay);
      };
    }

    open();
    return () => {
      cancelled = true;
      sourceRef.current?.close();
      sourceRef.current = null;
    };
  }, [code]);

  return { split, setSplit, connected, error };
}

/* ----------------------------- Formatting ----------------------------- */

export function formatMoney(cents, currency = "USD") {
  if (cents == null || Number.isNaN(cents)) return "—";
  try {
    return new Intl.NumberFormat(undefined, {
      style: "currency",
      currency,
    }).format(cents / 100);
  } catch {
    return `$${(cents / 100).toFixed(2)}`;
  }
}

/* ---------------------- Payment deep-link builders --------------------- */

/**
 * Returns an array of `{ method, label, url, available }` for each
 * payment option the host has filled in. The frontend renders these
 * as buttons.
 */
export function buildPaymentLinks(split, participantTotalCents) {
  const handles = split?.host_payment_handles || {};
  const amount = (participantTotalCents / 100).toFixed(2);
  const memo = encodeURIComponent(
    `${split?.host_name || "Host"} bill split · ${(split?.code || "").toUpperCase()}`
  );
  const links = [];

  if (handles.venmo) {
    const user = handles.venmo.replace(/^@/, "").trim();
    links.push({
      method: "venmo",
      label: `Pay $${amount} on Venmo`,
      url: `venmo://paycharge?txn=pay&recipients=${encodeURIComponent(user)}&amount=${amount}&note=${memo}`,
      webFallback: `https://venmo.com/${encodeURIComponent(user)}?txn=pay&amount=${amount}&note=${memo}`,
    });
  }
  if (handles.cashapp) {
    const tag = handles.cashapp.replace(/^\$/, "").trim();
    links.push({
      method: "cashapp",
      label: `Pay $${amount} on Cash App`,
      url: `https://cash.app/$${encodeURIComponent(tag)}/${amount}`,
    });
  }
  if (handles.apple_pay_phone) {
    // Apple Pay Cash has no public peer-to-peer deep-link.
    // iMessage is the canonical entry — opens with amount pre-filled in the
    // message body so the user can tap the Apple Pay puck once iMessage opens.
    const phone = handles.apple_pay_phone.replace(/[^0-9+]/g, "");
    links.push({
      method: "apple_pay",
      label: `Pay $${amount} via iMessage`,
      url: `sms:${phone}&body=${memo}%20%24${amount}`,
    });
  }
  if (handles.zelle) {
    // Zelle has no deep-link standard — fall back to emailing the host with
    // pre-filled details. Most banks support paste-amount workflows.
    links.push({
      method: "zelle",
      label: `Pay $${amount} via Zelle`,
      url: `mailto:${encodeURIComponent(handles.zelle)}?subject=Zelle%20%24${amount}&body=${memo}`,
    });
  }
  return links;
}

/* ----------------------------- Participant helpers --------------------- */

/**
 * Read the participant id this device joined the given split as,
 * if any. Stored in localStorage keyed by code so the same browser
 * reopening the link is recognised as the existing participant.
 */
export function getStoredParticipantId(code) {
  try {
    return localStorage.getItem(`tiplingo_split_${code.toUpperCase()}_pid`);
  } catch {
    return null;
  }
}

export function storeParticipantId(code, pid, name) {
  try {
    localStorage.setItem(`tiplingo_split_${code.toUpperCase()}_pid`, pid);
    if (name) {
      localStorage.setItem(`tiplingo_split_${code.toUpperCase()}_name`, name);
    }
  } catch {
    /* no-op */
  }
}

export function getStoredParticipantName(code) {
  try {
    return localStorage.getItem(`tiplingo_split_${code.toUpperCase()}_name`);
  } catch {
    return null;
  }
}

/* ----------------------------- Status labels -------------------------- */

export function statusLabel(status) {
  switch (status) {
    case "open":         return "Open";
    case "all_claimed":  return "All items claimed · waiting on payments";
    case "paid_in_full": return "Paid in full";
    case "closed":       return "Closed";
    default:             return status || "Open";
  }
}
